import Text from 'components/Text/Text';
import React, { useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IItemForGetShortInfoWithStatus, UserRoles } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useArchivedMultiSelect } from 'hooks/useArchivedMultiSelect';
import { Checkbox } from 'antd';
import SearchField from 'components/SearchField/SearchField';
import { ISelectOption } from 'components/Select/types';
import FormSelect from 'components/Select/FormSelect';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	data: IItemForGetShortInfoWithStatus[];
	setIDsOfRestaurantsAvailableForRepetition: (value: string[]) => void;
	IDSelectedCustomer: Nullable<string>;
	IDsOfRestaurantsAvailableForRepetition: string[];
	IDsSelectedRestaurant: string[];
	initialIDsOfRestaurantsAvailableForRepetition: string[];
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	setIDsOfRestaurantsAvailableForRepetition,
	IDSelectedCustomer,
	IDsOfRestaurantsAvailableForRepetition,
	IDsSelectedRestaurant,
	initialIDsOfRestaurantsAvailableForRepetition,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const [searchString, setSearchString] = useState<string>('');

	const restToHide = useMemo(
		() => data.filter((el) => IDsSelectedRestaurant.includes(el.id)),
		[data, IDsSelectedRestaurant],
	);

	const { showArchived, toggleShowArchived, visibleItems, options } = useArchivedMultiSelect({
		items: restToHide,
		entityType: 'restaurants',
		selectedIds: IDsOfRestaurantsAvailableForRepetition,
	});

	const filteredData = useMemo(() => {
		return searchString
			? options.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
			: options;
	}, [searchString, options]);

	const mappedOptions = useMemo<ISelectOption[]>(() => {
		const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
		return filteredData.map((el) => ({
			...el,
			disabled: !hasAccess && !accessibleRestaurantIds?.some((id) => id === el.id),
		}));
	}, [filteredData, loggedUser, hasAccess]);

	const handleSelectAllData = useCallback(() => {
		if (hasAccess) {
			if (visibleItems.length && IDsOfRestaurantsAvailableForRepetition.length === visibleItems.length) {
				setIDsOfRestaurantsAvailableForRepetition([]);
			} else {
				setIDsOfRestaurantsAvailableForRepetition(visibleItems.map((el) => el.id));
			}
		} else {
			const accessibleRestaurantIds = loggedUser.restaurants ? loggedUser.restaurants.map((el) => el.id) : [];
			const visibleIds = visibleItems.map((el) => el.id);
			const filterAccessibleRestaurantIds = accessibleRestaurantIds.filter((el) => visibleIds.includes(el));
			const isCheckedAll = filterAccessibleRestaurantIds.length
				? filterAccessibleRestaurantIds.every((el) => IDsOfRestaurantsAvailableForRepetition.includes(el))
				: false;
			if (isCheckedAll) {
				const filterIsDisabledInRestaurants = initialIDsOfRestaurantsAvailableForRepetition.filter(
					(el) => !accessibleRestaurantIds.includes(el),
				);
				setIDsOfRestaurantsAvailableForRepetition(filterIsDisabledInRestaurants);
			} else {
				const filteredLoggedUserRestaurants = loggedUser.restaurants
					? loggedUser.restaurants?.filter((el) => filterAccessibleRestaurantIds.includes(el.id))
					: [];
				const filterFilteredLoggedUserRestaurants = filteredLoggedUserRestaurants.filter(
					(el) => !IDsOfRestaurantsAvailableForRepetition.includes(el.id),
				);
				setIDsOfRestaurantsAvailableForRepetition([
					...IDsOfRestaurantsAvailableForRepetition,
					...filterFilteredLoggedUserRestaurants.map((el) => el.id),
				]);
			}
		}
	}, [
		hasAccess,
		IDsOfRestaurantsAvailableForRepetition,
		visibleItems,
		setIDsOfRestaurantsAvailableForRepetition,
		loggedUser,
		initialIDsOfRestaurantsAvailableForRepetition,
	]);

	useEffect(() => {
		setSearchString('');
	}, [IDSelectedCustomer, IDsSelectedRestaurant]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			const getIsCheckedByHasAccess = () => {
				return !!(visibleItems.length && visibleItems.length === IDsOfRestaurantsAvailableForRepetition.length);
			};

			const getIsCheckedByWithoutAccess = () => {
				const accessibleRestaurantIds = loggedUser.restaurants ? loggedUser.restaurants.map((el) => el.id) : [];
				const visibleIds = visibleItems.map((el) => el.id);
				const filterAccessibleRestaurantIds = accessibleRestaurantIds.filter((el) => visibleIds.includes(el));
				return filterAccessibleRestaurantIds.length
					? filterAccessibleRestaurantIds.every((el) => IDsOfRestaurantsAvailableForRepetition.includes(el))
					: false;
			};

			const getDisabledStatus = () => {
				const accessibleRestaurantIds = loggedUser.restaurants ? loggedUser.restaurants.map((el) => el.id) : [];
				const visibleIds = visibleItems.map((el) => el.id);
				return visibleIds.some((el) => accessibleRestaurantIds.includes(el));
			};

			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={hasAccess ? getIsCheckedByHasAccess() : getIsCheckedByWithoutAccess()}
							disabled={hasAccess ? !visibleItems.length : !getDisabledStatus()}
							onChange={handleSelectAllData}>
							<Text
								translationNameSpace="form"
								translationKey="selectAllRestaurantsLabel"
								asFragment={true}
							/>
						</Checkbox>
						<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
							<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[
			visibleItems,
			IDsOfRestaurantsAvailableForRepetition,
			loggedUser,
			hasAccess,
			handleSelectAllData,
			setSearchString,
			searchString,
			showArchived,
			toggleShowArchived,
		],
	);

	return (
		<FormSelect
			testId="MultipleSelect"
			name="IDsOfRestaurantsAvailableForRepetition"
			label="restaurantsLabel"
			options={mappedOptions}
			disabled={!IDsSelectedRestaurant.length}
			onChange={setIDsOfRestaurantsAvailableForRepetition}
			mode="multiple"
			counterValue={IDsOfRestaurantsAvailableForRepetition.length}
			dropdownRender={renderSelectMenu}
		/>
	);
};

export default React.memo(MultipleSelect);
